import { DerbyRngV1 } from '../rng';
import { DRILL_TICK_S, MASK_HELD, type MinigameDef, type MinigameSession, type SessionScore } from './framework';

export interface FurlongSegment { startTick: number; endTick: number; work: boolean }

/** Speed intervals: push through green furlongs, genuinely recover between them. */
export class FurlongsSession implements MinigameSession {
  readonly durationTicks = Math.round(15 / DRILL_TICK_S);
  readonly segments: FurlongSegment[];
  tick = 0;
  pace = 0.2;
  heat = 0;
  failed = false;
  private workScore = 0;
  private workTicks = 0;
  private recoveryScore = 0;
  private recoveryTicks = 0;

  constructor(readonly tier: number, seed: number) {
    const rng = new DerbyRngV1(seed).fork('furlongs');
    const segments: FurlongSegment[] = [];
    let at = 0;
    // Open on an active interval. Starting with a second of "do nothing"
    // made the drill read as unresponsive before the player saw its loop.
    let work = true;
    while (at < this.durationTicks) {
      const seconds = work ? rng.range(1.4, 2.2) : rng.range(0.8, 1.4);
      const endTick = Math.min(this.durationTicks, at + Math.round(seconds / DRILL_TICK_S));
      segments.push({ startTick: at, endTick, work });
      at = endTick;
      work = !work;
    }
    this.segments = segments;
  }

  get finished(): boolean { return this.failed || this.tick >= this.durationTicks; }
  segmentNow(): FurlongSegment { return this.segments.find((s) => s.startTick <= this.tick && this.tick < s.endTick) ?? this.segments.at(-1)!; }

  step(mask: number): void {
    if (this.finished) return;
    const held = (mask & MASK_HELD) !== 0;
    const work = this.segmentNow().work;
    const difficulty = Math.max(1, Math.min(4, this.tier));
    if (held) {
      this.pace = Math.min(1, this.pace + (0.72 + difficulty * 0.04) * DRILL_TICK_S);
      this.heat += (work ? 0.055 : 0.15) * difficulty * DRILL_TICK_S;
    } else {
      this.pace = Math.max(0.15, this.pace - 0.35 * DRILL_TICK_S);
      this.heat = Math.max(0, this.heat - 0.22 * DRILL_TICK_S);
    }
    if (work) {
      this.workTicks += 1;
      this.workScore += this.pace * (1 - Math.max(0, this.heat - 0.75));
    } else {
      this.recoveryTicks += 1;
      this.recoveryScore += held ? 0 : 1;
    }
    if (this.heat >= 1) this.failed = true;
    this.tick += 1;
  }

  score(): SessionScore {
    const work = this.workTicks ? this.workScore / this.workTicks : 0;
    const recovery = this.recoveryTicks ? this.recoveryScore / this.recoveryTicks : 0;
    const raw = Math.max(0, Math.min(1, 0.75 * work + 0.25 * recovery));
    return { raw, detail: `${Math.round(work * 100)}% work pace · ${Math.round(recovery * 100)}% clean recovery${this.failed ? ' · overheated' : ''}`, failed: this.failed };
  }
}

export const FURLONGS_DEF: MinigameDef = {
  id: 'furlongs', name: 'Flying Furlongs', stat: 'speed', energyCost: 4,
  create: (tier, seed) => new FurlongsSession(tier, seed),
};
